import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import {
  catchError, concatMap,
  filter,
  finalize, map,
  Observable,
  of,
  pairwise,
  startWith,
  Subscription,
  take,
  tap,
} from 'rxjs';

import { InviteRole, InviteStatus } from '@shared/invite/enums';
import { Invite, InviteRequest } from '@shared/invite/models';
import { Location } from '@shared/location/models';
import { AuthUser } from '@shared/signup/models';
import { UserAccountRole } from '@shared/user/enums';
import { CheckboxGroup } from '@tablesready/checkbox-group';
import { ButtonColorsEnum } from '@tablesready/button-group-control';
import {
  InfoModalComponent,
  ValidationService,
  UserWithAccount,
  NAME_PATTERN,
  emailValidator,
} from '@tablesready/common-ui';
import { InviteApiService } from '../../../../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-account-invite-user',
  templateUrl: './invite-user.component.html',
  styleUrls: ['./invite-user.component.scss'],
})
export class InviteUserComponent implements OnInit, OnDestroy {
  @Input() data: Observable<[UserWithAccount[], Location[], Invite[]]>;
  @Input() currentUser: AuthUser;

  icon = AppIcon;
  color = AppIconColor;
  buttonColors = ButtonColorsEnum;
  inProgress = false;
  locationsGroup: CheckboxGroup[];
  roles = [
    {
      name: 'User',
      value: InviteRole.user,
    },
    {
      name: 'Manager',
      value: InviteRole.manager,
    },
    {
      name: 'Administrator',
      value: InviteRole.admin,
    },
  ];

  form: FormGroup;

  private locations: Location[] = [];
  private users: UserWithAccount[] = [];
  private admins: UserWithAccount[] = [];
  private invites: Invite[] = [];
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly fb: FormBuilder,
    private readonly alertsService: AlertsService,
    private readonly validationService: ValidationService,
    private readonly modalController: FrontendModalService,
    private readonly inviteApiService: InviteApiService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      email: [
        '',
        {
          validators: Validators.required,
          asyncValidators: emailValidator(this.validationService),
          updateOn: 'blur',
        },
      ],
      locations: [[]],
      name: ['', [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern(NAME_PATTERN),
      ]],
      role: [InviteRole.user, [Validators.required]],
    });

    const roleChangesSub = this.roleControl
      .valueChanges
      .pipe(
        startWith(InviteRole.user),
        pairwise(),
        tap(([prev, current]) => {
          if (current === InviteRole.admin) {
            this.locationsControl.setValue(null);
          }

          if (prev === InviteRole.admin) {
            this.locationsControl.setValue([]);
          }
        }),
      ).subscribe();
    this.subscriptions.add(roleChangesSub);

    const emailChangeSub = this.emailControl
      .valueChanges
      .pipe(
        tap(value => {
          const alreadyExistsUser = this.users.find(user => user.email === value);
          const isCurrentUSer = this.currentUser.email === alreadyExistsUser?.email;

          if (alreadyExistsUser) {
            let message = `${alreadyExistsUser.name || 'This user'}  is already a user in your account. Please click below to add the location to the user.`;

            if (isCurrentUSer) {
              message = 'You\'re already assigned to this account';
            }

            this.modalController.create<InfoModalComponent, boolean>(InfoModalComponent, {
              message,
              displayButton: !isCurrentUSer,
              buttonText: `Go to ${alreadyExistsUser.name || 'user'} information`,
            })
              .pipe(
                take(1),
                tap(() => {
                  this.form.reset({
                    name: '',
                    email: '',
                    role: InviteRole.user,
                    locations: [],
                  });
                }),
                filter(data => !!data),
                concatMap(() => this.modalController.dismissAsync()),
                tap(() => {
                  this.modalController.dismiss(alreadyExistsUser);
                }),
              )
              .subscribe();
          }
        }),
      ).subscribe();
    this.subscriptions.add(emailChangeSub);

    const dataSubscription = this.data
      .pipe(tap(([users, locations, invites]) => {
        this.users = users.filter(user => user.role === UserAccountRole.user);
        this.admins = users.filter(user => user.role === UserAccountRole.admin);
        this.locations = locations.filter(({enabled}) => enabled);
        this.locationsGroup = this.locations.map(location => ({
          name: location.name,
          value: location._id,
        }));
        this.invites = invites;
      }))
      .subscribe();
    this.subscriptions.add(dataSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submit(): void {
    this.validationService.validateForm(this.form);
    if (!this.form.valid || !this.locationsControlValid || this.inProgress) {
      return;
    }

    this.inProgress = true;
    const value = this.form.value;

    if (this.checkAdmin(value)) {
      return;
    }

    if (this.checkInvites(value)) {
      return;
    }

    this.sendInvite(value);
  }

  private checkAdmin(value: FormData): boolean {
    const admin = this.admins.find(admin => admin.email === value.email);
    if (admin) {
      this.alertsService.addInfo(`This user has an account administrator role. Unable to invite him.`);
      this.inProgress = false;

      return true;
    }

    return false;
  }

  private checkInvites(value: FormData): boolean {
    const invites = this.invites.filter(invite => invite.email === value.email && invite.status !== InviteStatus.declined);
    if (invites.length === 0) {
      return false;
    }

    if (invites.find(invite => invite.role === InviteRole.admin)) {
      this.alertsService.addInfo('This user has already been invited to become an account administrator. Please cancel the existing invite if you\'d like to change the user role.');
      this.inProgress = false;

      return true;
    }

    if (value.role === InviteRole.admin) {
      this.alertsService.addInfo(`The user has been already invited to the account. Please review the invites list and cancel the previous invite(s) if you’d like to change the user role.`);
      this.inProgress = false;

      return true;
    }

    const interceptedLocationIds: string[] = [];
    for (let i = 0; i < value.locations.length; i++) {
      for (let j = 0; j < invites.length; j++) {
        if (invites[j].locations.includes(value.locations[i])) {
          interceptedLocationIds.push(value.locations[i]);
        }
      }
    }

    if (interceptedLocationIds.length) {
      let locationNames: string[] = [];
      for (let i = 0; i < interceptedLocationIds.length; i++) {
        const location = this.locations.find(location => location._id === interceptedLocationIds[i]);
        if (location) {
          locationNames.push(location.name);
        }
      }

      this.alertsService.addInfo(`This user has already been invited to ${locationNames.join(', ')}. Please cancel the existing invite if you'd like to change the user role.`);
      this.inProgress = false;

      return true;
    }

    return false;
  }

  private sendInvite(value: FormData): void {
    const data: InviteRequest = {
      email: value.email,
      role: value.role,
      name: value.name,
      locations: value.locations,
    };

    this.inviteApiService.send(data)
      .pipe(
        tap(() => {
          this.inProgress = false;
          this.alertsService.addSuccess('Invite successfully sent.', 'Success:');
          this.close();
        }),
        catchError(error => {
          if (error instanceof HttpErrorResponse && error.status === 409) {
            this.alertsService.addError(error.error.message);
          } else {
            this.alertsService.addError('An error occurred during sending invite.');
          }

          return of(null);
        }),
        finalize(() => this.inProgress = false),
      ).subscribe();
  }

  get emailControl(): AbstractControl {
    return this.form.get('email');
  }

  get fullNameControl(): AbstractControl {
    return this.form.get('name');
  }

  get roleControl(): AbstractControl {
    return this.form.get('role');
  }

  get isAdminRoleSelected(): boolean {
    return this.roleControl.value === InviteRole.admin;
  }

  get locationsControl(): AbstractControl {
    return this.form.get('locations');
  }

  get locationsControlValid(): boolean {
    return this.isAdminRoleSelected || this.locationsControl.value.length > 0;
  }

  close(): void {
    this.modalController.dismiss();
  }
}

interface FormData {
  email: string;
  name: string;
  role: InviteRole;
  locations: string[];
}
